const NORDIC_CHARACTER_MAP: Record<string, string> = {
  æ: 'ae',
  ø: 'o',
  å: 'a',
}

/**
 * Converts a display name into a URL/identifier-safe slug.
 * Handles Norwegian characters (æ, ø, å) and other diacritics.
 *
 * @example
 * slugify('Gran') // Returns: 'gran'
 * slugify('Bjørk, spesial') // Returns: 'bjork-spesial'
 */
export function slugify(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('no')
    .replace(/[æøå]/g, (char) => NORDIC_CHARACTER_MAP[char] ?? char)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function toSentenceCase(value: string): string {
  // Split on dots, underscores, or spaces, capitalize each word, then join with space
  return value
    .split(/[._\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function emptyToNull(value?: string | null): string | null {
  if (value === null || value === undefined || value.trim() === '') {
    return null
  }
  return value
}

/**
 * Formats a nullable/undefined string for display.
 * Returns the trimmed value or a fallback (default: '-').
 */
export function formatIdentifier(value?: string | null, fallback = '-'): string {
  return value?.trim() || fallback
}

/**
 * Returns the display name for a country code using Norwegian locale.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'NO', 'SE')
 * @param fallback - Fallback string when code is empty or not found (default: '-')
 * @returns Localized country name or fallback
 *
 * @example
 * getCountryName('NO') // Returns: 'Norge'
 * getCountryName('SE') // Returns: 'Sverige'
 * getCountryName('no') // Returns: 'Norge' (case-insensitive)
 */
export function getCountryName(countryCode?: string | null, fallback = '-'): string {
  if (!countryCode?.trim()) return fallback
  try {
    const normalizedCode = countryCode.trim().toUpperCase()
    const displayNames = new Intl.DisplayNames(['nb'], { type: 'region' })
    return displayNames.of(normalizedCode) ?? fallback
  } catch {
    return fallback
  }
}
